// Monitoring API client.

import { ApiError } from './client'
import type { GPUStats } from './gpu'

export interface SystemMetrics {
  cpu_percent: number
  memory_used_bytes: number
  memory_total_bytes: number
  disk_used_bytes: number
  disk_total_bytes: number
  load_avg_1: number
  load_avg_5: number
  load_avg_15: number
  uptime_seconds: number
  collected_at: number
}

export interface ContainerHealth {
  name: string
  state: string
  error_count_24h: number
}

export interface NodeMetrics {
  node_name: string
  system: SystemMetrics
  containers: ContainerHealth[]
  gpu?: GPUStats
}

export interface MetricPoint {
  node_name: string
  system: SystemMetrics
  gpu?: GPUStats
  collected_at: number
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  })
  if (res.status === 401) {
    if (!location.pathname.startsWith('/login')) {
      window.location.href = '/login'
    }
    throw new ApiError(401, 'unauthorized')
  }
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    throw new ApiError(
      res.status,
      (body as { error?: string } | null)?.error ?? res.statusText,
      body,
    )
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

export const monitorApi = {
  current: () => request<NodeMetrics[]>('/api/admin/monitoring/current'),

  history: (node: string, hours = 24) =>
    request<MetricPoint[]>(
      `/api/admin/monitoring/history?node=${node}&hours=${hours}`,
    ),
}
